import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { DimensionUnit, WeightUnit } from '@invenco/common-interface/shared';
import type { SkuDTO } from '@invenco/products-interface';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { FormDialogContent } from '@/components/forms/form-dialog';
import type { Result } from '@/shared/helpers/Result';

import { NumberFormField } from '../../../../components/forms/number-form-field';

const schema = z.object({
  length: z.number().min(0),
  width: z.number().min(0),
  height: z.number().min(0),
  weight: z.number().min(0),
});

type Props = {
  sku?: Partial<SkuDTO>;
  onSubmit: (data: Partial<SkuDTO>) => Promise<Result> | Promise<void> | void;
};

export function MeasurementsDialogContent({ sku, onSubmit }: Props) {
  const { defaultValues, measurementUnit, weightUnit } = useMemo(() => {
    const resolvedMeasurementUnit = sku?.measurement?.dimensionUnit ?? DimensionUnit.CM;
    const resolvedWeightUnit = sku?.measurement?.weightUnit ?? WeightUnit.KG;

    return {
      measurementUnit: resolvedMeasurementUnit,
      weightUnit: resolvedWeightUnit,
      defaultValues: {
        length: sku?.measurement?.length ?? 0,
        width: sku?.measurement?.width ?? 0,
        height: sku?.measurement?.height ?? 0,
        weight: sku?.measurement?.weight ?? 0,
      },
    };
  }, [
    sku?.measurement?.dimensionUnit,
    sku?.measurement?.height,
    sku?.measurement?.length,
    sku?.measurement?.weight,
    sku?.measurement?.weightUnit,
    sku?.measurement?.width,
  ]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  return (
    <FormDialogContent
      form={form}
      onSubmit={(data) =>
        onSubmit({
          measurement: {
            length: Number(data.length),
            width: Number(data.width),
            height: Number(data.height),
            weight: Number(data.weight),
            dimensionUnit: measurementUnit,
            weightUnit,
          },
        })
      }
    >
      <DialogHeader>
        <DialogTitle>Edit measurements</DialogTitle>
        <DialogDescription>Update dimensions and weight for this SKU.</DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:items-start">
          <NumberFormField
            control={form.control}
            name="length"
            label={`Length (${measurementUnit})`}
          />
          <NumberFormField
            control={form.control}
            name="width"
            label={`Width (${measurementUnit})`}
          />
          <NumberFormField
            control={form.control}
            name="height"
            label={`Height (${measurementUnit})`}
          />
          <NumberFormField control={form.control} name="weight" label={`Weight (${weightUnit})`} />
        </div>
      </DialogBody>
    </FormDialogContent>
  );
}
